import { useEffect } from 'preact/hooks';
import { route } from 'preact-router';
import { user, isAdmin, isSuperadmin, tecnicos, admins, tenant, success, error, editId } from '../store/state';
import { theme, setTheme, type Theme } from '../store/theme';
import {
  cambiarPw,
  toggleDisponibilidad,
  toggleMiDisponibilidad,
  eliminarTecnico,
  loadAdmins,
  eliminarAdmin,
} from '../store/actions/user.actions';
import { loadMyTenant, guardarLogoEmpresa, quitarLogoEmpresa } from '../store/actions/tenant.actions';
import { compressLogo } from '../features/image-compression/compressLogo';
import { Shell } from '../layout/Shell';
import { Icon } from '../components/Icon';

const THEME_OPTIONS: { value: Theme; label: string; icon: 'monitor' | 'sun' | 'moon' }[] = [
  { value: 'system', label: 'Sistema', icon: 'monitor' },
  { value: 'light', label: 'Claro', icon: 'sun' },
  { value: 'dark', label: 'Oscuro', icon: 'moon' },
];

export function PerfilView() {
  useEffect(() => {
    if (isAdmin.value) {
      loadAdmins();
      loadMyTenant();
    }
  }, []);

  const onLogoChange = async (e: Event) => {
    const file = (e.currentTarget as HTMLInputElement).files?.[0];
    if (!file) return;
    const dataUrl = await compressLogo(file);
    await guardarLogoEmpresa(dataUrl);
  };

  const onPasswordSubmit = async (e: Event) => {
    e.preventDefault();
    const fd = new FormData(e.currentTarget as HTMLFormElement);
    try {
      await cambiarPw(String(fd.get('current')), String(fd.get('nueva')));
      success.value = 'Contraseña actualizada.';
      (e.currentTarget as HTMLFormElement).reset();
    } catch (err) {
      error.value = (err as Error).message;
    }
  };

  const u = user.value!;

  return (
    <Shell tab="perfil">
      <h2>Configuración</h2>
      <div class="card">
        <div class="row">
          <div>
            <strong>{u.nombre}</strong>
            <p class="muted" style={{ margin: '2px 0 0' }}>
              {u.email} · {u.rol}
            </p>
          </div>
        </div>
        <button
          class="secondary"
          style={{ marginTop: 12, padding: '8px 12px', fontSize: 13, width: 'auto' }}
          onClick={() => route('/perfil/editar')}
        >
          <Icon name="pencil" size={14} />
          Editar mi perfil
        </button>
      </div>

      <h3 style={{ margin: '20px 0 8px' }}>Apariencia</h3>
      <div class="card">
        <p class="muted" style={{ margin: '0 0 4px' }}>
          Tema de la app
        </p>
        <div class="theme-toggle">
          {THEME_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              class={theme.value === opt.value ? 'active' : ''}
              onClick={() => setTheme(opt.value)}
              type="button"
            >
              <Icon name={opt.icon} size={14} />
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {!isAdmin.value && !isSuperadmin.value && (
        <>
          <h3 style={{ margin: '20px 0 8px' }}>Disponibilidad</h3>
          <div class="card">
            <div class="row">
              <div>
                <strong>{u.disponible ? 'Estás disponible' : 'No estás disponible'}</strong>
                <p class="muted" style={{ margin: '2px 0 0' }}>
                  {u.disponible
                    ? 'Podés tomar visitas del pool y te pueden asignar nuevas.'
                    : 'No te van a asignar visitas nuevas mientras estés así.'}
                </p>
              </div>
              <span class={'badge ' + (u.disponible ? 'al_dia' : 'vencida')}>
                {u.disponible ? 'Disponible' : 'No disp.'}
              </span>
            </div>
            <button
              class={u.disponible ? 'danger-outline' : 'primary'}
              onClick={() => toggleMiDisponibilidad(!u.disponible)}
            >
              <Icon name="user-round" size={16} />
              {u.disponible ? 'Marcarme no disponible' : 'Marcarme disponible'}
            </button>
          </div>
        </>
      )}

      <h3 style={{ margin: '20px 0 8px' }}>Cambiar contraseña</h3>
      <form class="form-desktop" onSubmit={onPasswordSubmit}>
        <label>Actual</label>
        <input name="current" type="password" required />
        <label>Nueva</label>
        <input name="nueva" type="password" required minLength={6} />
        <button class="primary" type="submit">
          Cambiar
        </button>
      </form>

      {isAdmin.value && (
        <>
          <h3 style={{ margin: '20px 0 8px' }}>Logo de la empresa</h3>
          <div class="card">
            <p class="muted" style={{ margin: '0 0 8px' }}>
              Aparece en los reportes y emails que se envían a tus clientes.
            </p>
            {tenant.value?.logoUrl && (
              <img
                src={tenant.value.logoUrl}
                alt="Logo"
                style={{ maxHeight: 64, maxWidth: 220, marginBottom: 12, display: 'block' }}
              />
            )}
            <input type="file" accept="image/png,image/jpeg,image/webp" onChange={onLogoChange} />
            {tenant.value?.logoUrl && (
              <button
                class="danger-outline"
                style={{ marginTop: 8 }}
                onClick={quitarLogoEmpresa}
              >
                <Icon name="trash-2" size={13} />
                Quitar logo
              </button>
            )}
          </div>

          <h3 style={{ margin: '20px 0 8px' }}>Técnicos</h3>
          {tecnicos.value.length ? (
            tecnicos.value.map((t) => (
              <div class="card" key={t.id}>
                <div class="row">
                  <div>
                    <strong>{t.nombre}</strong>
                    <p class="muted" style={{ margin: '2px 0' }}>
                      {t.email}
                      {t.zona ? ' · ' + t.zona : ''}
                      {t.telefono ? ' · ' + t.telefono : ''}
                    </p>
                  </div>
                  <span class={'badge ' + (t.disponible ? 'al_dia' : 'vencida')}>
                    {t.disponible ? 'Disponible' : 'No disp.'}
                  </span>
                </div>
                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  <button
                    class="secondary"
                    style={{ flex: 1, padding: 6, fontSize: 13, marginTop: 0 }}
                    onClick={() => {
                      editId.value = t.id;
                      route(`/perfil/tecnicos/${t.id}/editar`);
                    }}
                  >
                    <Icon name="pencil" size={13} />
                    Editar
                  </button>
                  <button
                    class="secondary"
                    style={{ flex: 1, padding: 6, fontSize: 13, marginTop: 0 }}
                    onClick={() => toggleDisponibilidad(t.id, !t.disponible)}
                  >
                    <Icon name="user-round" size={13} />
                    {t.disponible ? 'No disponible' : 'Disponible'}
                  </button>
                  <button
                    class="danger-outline"
                    style={{ flex: 1, padding: 6, fontSize: 13, marginTop: 0 }}
                    onClick={() => eliminarTecnico(t.id)}
                  >
                    <Icon name="trash-2" size={13} />
                    Desactivar
                  </button>
                </div>
              </div>
            ))
          ) : (
            <div class="empty">
              <Icon name="user-round" />
              <p>Todavía no agregaste técnicos.</p>
            </div>
          )}
          <button class="secondary" onClick={() => route('/perfil/tecnicos/nuevo')}>
            <Icon name="user-plus" size={16} />
            Agregar técnico
          </button>

          <h3 style={{ margin: '20px 0 8px' }}>Administradores</h3>
          {admins.value.length ? (
            admins.value.map((a) => (
              <div class="card" key={a.id}>
                <div class="row">
                  <div>
                    <strong>{a.nombre}</strong>
                    <p class="muted" style={{ margin: '2px 0' }}>
                      {a.email}
                      {a.telefono ? ' · ' + a.telefono : ''}
                    </p>
                  </div>
                  {a.id === user.value?.id && <span class="badge al_dia">Vos</span>}
                </div>
                {a.id !== user.value?.id && (
                  <button
                    class="danger-outline"
                    style={{ marginTop: 8 }}
                    onClick={() => eliminarAdmin(a.id)}
                  >
                    <Icon name="trash-2" size={13} />
                    Desactivar
                  </button>
                )}
              </div>
            ))
          ) : (
            <div class="empty">
              <Icon name="user-round" />
              <p>Cargando administradores…</p>
            </div>
          )}
          <button class="secondary" onClick={() => route('/perfil/admins/nuevo')}>
            <Icon name="user-plus" size={16} />
            Agregar administrador
          </button>
        </>
      )}
    </Shell>
  );
}
